import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const specialties = [
  'Harmonização Facial',
  'Blefaroplastia',
  'Lifting Facial',
  'Terapia Capilar',
  'Bioestimuladores',
];

const SpecialistsHero: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.2, triggerOnce: false });
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    // Ensure activeIndex stays within bounds if specialties array changes
    setActiveIndex((prev) => prev % specialties.length);

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % specialties.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [specialties.length]);

  const handleScrollToList = () => {
    const el = document.getElementById('lista-especialistas');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      ref={elementRef as React.RefObject<HTMLElement>}
      className="relative bg-gradient-to-br from-[#E8E4DF] via-[#EBE7E2] to-[#E0DCD7] pt-28 md:pt-32 lg:pt-36 pb-14 md:pb-18 lg:pb-20 overflow-hidden"
    >
      {/* Animated background overlay */}
      <div className="absolute inset-0">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-gradient-to-br from-secondary/15 to-transparent blur-3xl animate-float" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-tl from-primary/15 to-transparent blur-3xl animate-float-delay" />
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-secondary/40 to-transparent opacity-70" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left - Content */}
          <div className={`lg:col-span-6 transition-all duration-700 ${isVisible ? 'opacity-100 animate-slide-in-left' : 'opacity-0 -translate-x-6'}`}> 
            <div className="max-w-xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight">
                <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 bg-clip-text text-transparent">Especialistas</span>
              </h1>
              <p className="mt-4 text-base md:text-lg text-gray-700 leading-relaxed">
                Conheça nossa equipe de especialistas e os procedimentos que cada profissional realiza. Cuidamos de você com
                técnica, sutileza e o padrão de excelência da clínica.
              </p>

              {/* Rotating specialties */}
              <div className="mt-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 border border-gray-200 shadow-sm">
                  <span className="text-xs font-semibold tracking-wider text-gray-600">Especialidade em destaque</span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-secondary/80 to-primary/80 text-white text-xs font-bold animate-fade-in">
                    {specialties[activeIndex]}
                  </span>
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleScrollToList}
                  className="group relative inline-flex items-center justify-center gap-3 px-7 py-3 bg-dark text-white font-medium text-sm md:text-base tracking-wide overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 border-2 border-dark hover:border-secondary rounded-full"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-secondary to-primary transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 relative z-10 group-hover:scale-110 transition-transform duration-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span className="relative z-10 font-semibold">Ver lista completa</span>
                </button>

                <Link
                  to="/agendar"
                  className="inline-flex items-center justify-center gap-3 px-7 py-3 bg-white text-dark font-semibold border-2 border-dark rounded-full hover:border-secondary hover:text-secondary transition-all hover:scale-105"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Agendar avaliação
                </Link>
              </div>

              {/* Bottom helper text */}
              <div className="mt-6 text-sm text-gray-600 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-secondary/70 animate-pulse" />
                <span>Role para explorar nossa equipe e especialidades.</span>
              </div>
            </div>
          </div>

          {/* Right - Visual gallery */}
          <div className={`lg:col-span-6 relative transition-all duration-700 ${isVisible ? 'opacity-100 animate-slide-in-right' : 'opacity-0 translate-x-6'}`}>
            <div className="relative">
              {/* Main image */}
              <div className="relative bg-white p-3 md:p-4 rounded-3xl shadow-2xl transform transition-all duration-700 hover:scale-[1.02]">
                <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden bg-gray-100">
                  <img
                    src={encodeURI('/assets/img/nfotos/Arlley 6.jpg')}
                    alt="Ambiente da clínica"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Floating small image 1 */}
              <div className="absolute -bottom-6 -left-4 md:-bottom-8 md:-left-6 w-36 h-36 md:w-48 md:h-48">
                <div className="bg-white p-2 md:p-3 rounded-2xl shadow-xl h-full transform transition-all duration-500 hover:scale-110 hover:rotate-2 border border-gray-100 animate-float">
                  <div className="rounded-xl overflow-hidden h-full bg-gray-100">
                    <img src="/assets/img/IMG_1697.JPEG" alt="Recepção" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>

              {/* Floating small image 2 */}
              <div className="absolute -top-6 -right-6 md:-top-8 md:-right-8 w-28 h-28 md:w-36 md:h-36">
                <div className="bg-white p-2 rounded-2xl shadow-xl h-full transform transition-all duration-500 hover:scale-110 hover:-rotate-2 border border-gray-100 animate-float-delay">
                  <div className="rounded-xl overflow-hidden h-full bg-gray-100">
                    <img src="/assets/img/tambia.jpg" alt="Equipe" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>

              {/* Badge */}
              <div className="absolute top-4 left-4">
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-secondary" />
                  <span className="text-xs font-semibold text-gray-700">Equipe multidisciplinar</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-secondary/30 to-transparent" />
    </section>
  );
};

export default SpecialistsHero;